import { WifiOff, Cloud, RefreshCw, AlertCircle } from "lucide-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useLatestSyncTime } from "@/hooks/useLastSyncTime";
import { useAuth } from "@/hooks/useAuth";
import { OFFLINE_QUERY_KEYS } from "@/lib/offline";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { format } from "date-fns";
import { fi } from "date-fns/locale";

/**
 * Offline indicator banner.
 * Shows when the app is offline with last sync time.
 * Also shows syncing status and failed actions warnings.
 */
export function OfflineIndicator() {
  const { isOnline } = useOnlineStatus();
  const { user } = useAuth();
  const { pendingCount, isSyncing, failedActions } = useOfflineSync();
  
  // Get last sync time from offline queries
  const lastSyncTime = useLatestSyncTime([...OFFLINE_QUERY_KEYS], user?.id);

  // Show syncing indicator when online and syncing
  if (isOnline && isSyncing) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 bg-primary text-primary-foreground 
                   py-2 px-4 z-50 flex items-center justify-center gap-2 text-sm font-medium
                   animate-in slide-in-from-top duration-300"
        role="status"
        aria-live="polite"
      >
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>Synkronoidaan...</span>
      </div>
    );
  }

  // Show failed actions warning when online
  if (isOnline && failedActions.length > 0) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 bg-amber-500 text-white 
                   py-2 px-4 z-50 flex items-center justify-center gap-2 text-sm font-medium
                   animate-in slide-in-from-top duration-300"
        role="alert"
        aria-live="assertive"
      >
        <AlertCircle className="h-4 w-4" />
        <span>{failedActions.length} toimintoa epäonnistunut</span>
      </div>
    );
  }

  // Hide when online and nothing pending
  if (isOnline && pendingCount === 0) return null;

  // Show offline indicator
  if (!isOnline) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 bg-destructive text-destructive-foreground 
                   py-2 px-4 z-50 flex items-center justify-center gap-2 text-sm font-medium
                   animate-in slide-in-from-top duration-300"
        role="alert"
        aria-live="assertive"
      >
        <WifiOff className="h-4 w-4" />
        <span>Offline-tila</span>
        {pendingCount > 0 && (
          <span className="opacity-75">
            – {pendingCount} toimintoa odottaa
          </span>
        )}
        {pendingCount === 0 && lastSyncTime && (
          <span className="opacity-75">
            – data synkronoitu {format(lastSyncTime, "HH:mm", { locale: fi })}
          </span>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Small sync status icon for use in headers/toolbars.
 */
interface SyncStatusBadgeProps {
  pendingCount?: number;
}

export function SyncStatusBadge({ pendingCount = 0 }: SyncStatusBadgeProps) {
  const { isOnline } = useOnlineStatus();

  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      {isOnline ? (
        <Cloud className="h-5 w-5 text-muted-foreground animate-pulse" />
      ) : (
        <WifiOff className="h-5 w-5 text-destructive" />
      )}
      {pendingCount > 0 && (
        <span 
          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive 
                     text-[10px] font-bold text-destructive-foreground flex items-center justify-center"
        >
          {pendingCount > 9 ? '9+' : pendingCount}
        </span>
      )}
    </div>
  );
}
